"use client";

import { Danger } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { XIcon } from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/use-media-query";

interface NoReferrerConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Confirmation shown when a buyer completes a purchase without a referrer
 * (their MLM معرف). On V2, a blank referrer on a first purchase is a
 * permanent, irreversible election out of the referral network — there is
 * no house-account fallback — so we make the buyer explicitly opt in before
 * continuing. Mirrors the design-system pattern of {@link HighGasPriceModal}.
 *
 * skill-ack: bug fix only, reusing existing Dialog/Drawer primitives (the
 * project's established responsive-modal pattern, e.g.
 * features/staking/ResponsiveModal.tsx), no new UI/components.
 *
 * Renders as a Drawer on mobile, not a Dialog: Drawer content uses z-110
 * while Dialog uses z-50 (see components/ui/drawer.tsx and dialog.tsx). This
 * confirmation can open while the purchase flow itself is presented in a
 * Drawer, so a plain Dialog here rendered behind it and was unusable.
 */
export const NoReferrerConfirmModal = ({
  open,
  onOpenChange,
  onProceed,
  onCancel,
  isLoading = false,
}: NoReferrerConfirmModalProps) => {
  const t = useTranslations("modals");
  const isMobile = useMediaQuery("(max-width: 767px)");

  const icon = (
    <Danger size={24} variant="Bold" className="text-semantic-alert" />
  );
  const proceedButton = (
    <Button
      className="w-full"
      variant="solid"
      colors="secondary"
      onClick={onProceed}
      loading={isLoading}
    >
      {t("noReferrer.proceedButton")}
    </Button>
  );
  const cancelButton = (
    <Button
      className="w-full"
      variant="ghost"
      colors="default"
      onClick={onCancel}
      disabled={isLoading}
    >
      {t("noReferrer.cancelButton")}
    </Button>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="space-y-4 px-9 pb-8">
          <DrawerHeader className="w-full">
            <DrawerTitle className="flex items-center">
              <div className="flex items-center justify-center gap-3 w-full">
                {icon}
                <span className="text-lg font-semibold text-semantic-alert mt-1.5">
                  {t("noReferrer.title")}
                </span>
              </div>
              <DrawerClose asChild onClick={onCancel}>
                <XIcon size={24} className="text-text-disabled" />
              </DrawerClose>
            </DrawerTitle>
          </DrawerHeader>
          <DrawerDescription className="text-center text-sm text-text-primary font-normal">
            {t("noReferrer.message")}
          </DrawerDescription>
          <DrawerFooter className="flex flex-col gap-3 w-full">
            {proceedButton}
            {cancelButton}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs space-y-4 px-9">
        <DialogHeader className="w-full">
          <DialogTitle className="flex items-center">
            <div className="flex items-center justify-center gap-3 w-full">
              {icon}
              <span className="text-lg font-semibold text-semantic-alert mt-1.5">
                {t("noReferrer.title")}
              </span>
            </div>
            <DialogClose asChild onClick={onCancel}>
              <XIcon size={24} className="text-text-disabled" />
            </DialogClose>
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-center text-sm text-text-primary font-normal">
          {t("noReferrer.message")}
        </DialogDescription>
        <DialogFooter className="flex flex-col gap-3 w-full">
          {proceedButton}
          {cancelButton}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
